import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/lib/permissions";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  LayoutDashboard,
  MapPin,
  Image,
  Megaphone,
  Target,
  BarChart3,
  Receipt,
  Settings,
  Building2,
  Users,
  ChevronLeft,
  Gauge,
  ChevronRight,
  LogOut,
  Radio,
  Monitor,
  Brain,
  UserCircle,
  Archive,
  BookOpen,
  Tags,
  Tag,
  Palette,
  FileEdit,
  DollarSign,
  UsersRound,
  ShieldCheck,
  Sparkles, Video, Image as ImageIcon } from "lucide-react";

// Booma Partner Portal logo (black on transparent; inverted to white in dark theme)
const BOOMA_LOGO = "/booma-partner-portal-trans-bg.png";
// Booma symbol only (black on transparent) — shown when the sidebar is collapsed
const BOOMA_SYMBOL = "/favicon.png";

// `permission` (round 10) hides an item a role cannot use, reusing the same shape as
// advertiserOnly rather than adding a second mechanism. It hides a LINK; the routes
// behind each one are gated independently, so this is tidiness rather than security.
//
// Under Robin's R2 a FINANCE user's nav is Dashboard, Billing and Resources. That is
// the ruling working, not a bug: everything else would link to a page the API refuses.
const mainNavItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/campaigns", label: "Campaigns", icon: Megaphone, advertiserOnly: true, permission: "campaigns:read" },
  // "Archived" undersold it once drafts and expired campaigns moved in (#36, #37). A
  // merchant looking for the draft they were working on would not think to click
  // Archived, which is the whole failure mode this was meant to fix.
  { path: "/campaigns/archived", label: "Drafts & Archive", icon: Archive, advertiserOnly: true, subItem: true, permission: "campaigns:read" },
  // spec 4.3, 6.2, and Robin's structure from round 3. A heading rather than a link, so
  // every child is named.
  //
  // Creative Builder sits INSIDE Assets. Round 2 promoted it to top level because it was
  // buried under a heading that said Images and could not be found; with the heading
  // naming all five children that is solved, and a creative tool outside the group about
  // creative assets was always the odd part.
  //
  // Images is back as its own entry. Removing it was my own call, on the reasoning that
  // /creatives is a builder and not a library. That was right about the route and wrong
  // to leave no gallery: making something and looking at what you have are different
  // jobs, and Images is the second one.
  //
  // Videos is listed and disabled rather than omitted: the spec has it, V1 does not build
  // it, and a merchant who cannot see it has no way to know which of those is true.
  { label: "Assets", icon: Image, advertiserOnly: true, heading: true, permission: "content:read" },
  { path: "/creatives", label: "Creative builder", icon: Sparkles, advertiserOnly: true, subItem: true, permission: "content:write" },
  { path: "/creatives/images", label: "Images", icon: ImageIcon, advertiserOnly: true, subItem: true, permission: "content:read" },
  { path: "/creatives/videos", label: "Videos", icon: Video, advertiserOnly: true, subItem: true, comingSoon: true, permission: "content:read" },
  { path: "/creatives/assets", label: "Branding & logos", icon: Palette, advertiserOnly: true, subItem: true, permission: "content:read" },
  { path: "/creatives/offers", label: "Discovery offers", icon: Tag, advertiserOnly: true, subItem: true, permission: "campaigns:read" },
  { path: "/locations", label: "Locations", icon: MapPin, advertiserOnly: true, permission: "content:read" },
  { path: "/analytics", label: "Analytics", icon: BarChart3, permission: "analytics:read" },
  { path: "/billing", label: "Billing", icon: Receipt, permission: "billing:read" },
  { path: "/resources", label: "Resources", icon: BookOpen },
];

// Round 8, decision A3. This was nine flat entries mixing three kinds of thing, so an
// admin could not tell at a glance which were safe to click on production - Drive Panel,
// which hardcodes a demo passenger, sat beside Tier Pricing, which changes what
// merchants are charged.
//
// The BADGE matters more than the grouping. Grouping makes the list scannable; the badge
// is what stops somebody demonstrating the Drive Panel and believing it is live data.
//
// Subheadings follow the pattern the Assets group already uses in the advertiser nav.
const adminNavGroups = [
  {
    label: "Manage",
    items: [
      { path: "/admin/advertisers", label: "Advertisers", icon: Building2 },
      { path: "/admin/users", label: "Users", icon: Users },
    ],
  },
  {
    label: "Configure",
    items: [
      { path: "/admin/tier-pricing", label: "Tier Pricing", icon: DollarSign },
      { path: "/admin/ai-models", label: "AI Models", icon: Sparkles },
      { path: "/admin/ai-usage", label: "AI Usage", icon: Gauge },
      // Round 10. What each advertiser role may do, editable without a deploy.
      { path: "/admin/roles", label: "Role Permissions", icon: ShieldCheck },
      // Renamed from "Categories" (round 8, W5). It manages BUSINESS categories for
      // competitor matching, which is what the page itself has always been headed.
      // NOT "Venue Categories": venue TYPE is a separate thing, the Gold advanced
      // targeting axis, and the two would be read as the same control.
      { path: "/admin/categories", label: "Business Categories", icon: Tags },
    ],
  },
  {
    label: "Testing",
    items: [
      { path: "/attribution", label: "Attribution", icon: Brain },
      { path: "/ad-serving", label: "Ad Serving", icon: Radio, tag: "dev" },
      { path: "/drive-panel", label: "Drive Panel", icon: Monitor, external: true, tag: "demo" },
    ],
  },
];

const bottomNavItems = [
  { path: "/profile", label: "Profile", icon: UserCircle, advertiserOnly: true },
  { path: "/team", label: "Team", icon: UsersRound, advertiserOnly: true },
  { path: "/settings", label: "Settings", icon: Settings },
];

// Defined at module scope (not inside Sidebar) so the nav items don't remount on every render/toggle
function NavLink({ item, open, isActive }) {
  const Icon = item.icon;

  // A section heading, not a destination. Collapsed, it becomes a rule — a lone icon
  // that does nothing reads as a broken link.
  if (item.heading) {
    return open ? (
      <p className="px-3 pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
        {item.label}
      </p>
    ) : (
      <div className="border-t border-border mx-3 my-2" />
    );
  }

  // Listed so the merchant knows it is coming, disabled because it is not here yet.
  // Rendering it as a live link to an empty page would be worse than not listing it.
  if (item.comingSoon) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 ml-4",
          "text-muted-foreground/50 cursor-not-allowed"
        )}
        title={`${item.label} - coming soon`}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        {open && (
          <span className="font-medium text-xs flex items-center gap-1.5">
            {item.label}
            <span className="text-[9px] uppercase tracking-wide border border-current rounded px-1 py-px">
              Soon
            </span>
          </span>
        )}
      </div>
    );
  }

  // External links open in new tab
  if (item.external) {
    return (
      <a
        href={item.path}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200",
          "hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
        )}
        data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {open && (
          <span className="font-medium text-sm flex items-center gap-1.5">
            {item.label}
            {item.tag && (
              <span className="text-[9px] uppercase tracking-wide border border-current rounded px-1 py-px opacity-70">
                {item.tag}
              </span>
            )}
            <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </span>
        )}
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      className={cn(
        "flex items-center gap-3 rounded-lg transition-colors duration-200",
        "hover:bg-secondary/50",
        item.subItem ? "px-3 py-2 ml-4" : "px-3 py-2.5",
        isActive && "bg-primary/10 text-primary border border-primary/20",
        !isActive && "text-muted-foreground hover:text-foreground"
      )}
      data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
    >
      <Icon className={cn(
        "flex-shrink-0",
        item.subItem ? "h-4 w-4" : "h-5 w-5",
        isActive && "text-primary"
      )} />
      {open && (
        <span className={cn("font-medium flex items-center gap-1.5",
                            item.subItem ? "text-xs" : "text-sm")}>
          {item.label}
          {/* Round 8, A3. Marks a tool that simulates rather than does: Drive Panel
              hardcodes a demo passenger and sat beside controls that change what
              merchants are charged. Same styling as the Soon badge above, so the two
              read as one kind of marker rather than two inventions. */}
          {item.tag && (
            <span className="text-[9px] uppercase tracking-wide border border-current rounded px-1 py-px opacity-70">
              {item.tag}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}

export default function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { can } = usePermissions();
  const { theme } = useTheme();
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  return (
    <aside
      style={{ top: "var(--env-banner-h, 0px)", height: "calc(100vh - var(--env-banner-h, 0px))" }}
      className={cn(
        "fixed left-0 bg-card border-r border-border flex flex-col transition-[width] duration-300 z-50",
        open ? "w-64" : "w-20"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <Link to="/" className="flex items-center justify-center">
          <img
            src={open ? BOOMA_LOGO : BOOMA_SYMBOL}
            alt="Booma Partner Portal"
            className={cn(
              "rounded-lg",
              open ? "h-10 w-auto" : "h-9 w-9",
              theme === 'dark' && "invert"
            )}
          />
        </Link>
      </div>

      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-20 h-6 w-6 rounded-full border border-border bg-card shadow-md"
        onClick={() => setOpen(!open)}
        data-testid="sidebar-toggle"
      >
        {open ? <ChevronLeft className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </Button>

      {/* Main Nav */}
      {/* Round 7, SP-35: "grey up/down toggle bar is still here and should be
          remove". It was never a toggle - it is this container's scrollbar,
          which App.css styles as a rounded grey thumb, and the nav is tall
          enough to overflow on a normal laptop. So a bar that looks like a
          control sat permanently beside the menu and did nothing.

          The scrolling has to stay or the nav clips on a short viewport. Only
          the chrome goes: no-scrollbar hides the track and thumb in WebKit and
          Firefox while the container still scrolls by wheel and by keyboard. */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-1">
        {mainNavItems
          .filter(item => !item.advertiserOnly || !isAdmin)
          // Round 10. An item the role cannot use is a link to a page the API refuses,
          // so it goes rather than being shown and failing. Platform admins hold every
          // permission, so this changes nothing for them.
          .filter(item => !item.permission || can(item.permission))
          .map(item => (
            <NavLink key={item.path || item.label} item={item} open={open} isActive={location.pathname === item.path} />
          ))}

        {/* Admin Section, in three groups (round 8, A3) */}
        {isAdmin && adminNavGroups.map(group => (
          <div key={group.label}>
            <div className="pt-4 pb-2">
              {open ? (
                <p className="px-3 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  {group.label}
                </p>
              ) : (
                <div className="border-t border-border mx-3" />
              )}
            </div>
            {group.items.map(item => (
              <NavLink key={item.path} item={item} open={open} isActive={location.pathname === item.path} />
            ))}
          </div>
        ))}
      </div>

      {/* Bottom Nav */}
      <div className="p-3 border-t border-border space-y-1">
        {bottomNavItems
          .filter(item => !item.advertiserOnly || !isAdmin)
          .map(item => (
            <NavLink key={item.path} item={item} open={open} isActive={location.pathname === item.path} />
          ))}
        
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 px-3 py-2.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10",
            !open && "justify-center"
          )}
          onClick={() => setConfirmLogoutOpen(true)}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {open && <span className="font-medium text-sm">Logout</span>}
        </Button>
      </div>

      {/* User Info */}
      {open && user && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {user.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Confirm before logging out — prevents accidental logout. (#13) */}
      <AlertDialog open={confirmLogoutOpen} onOpenChange={setConfirmLogoutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log out?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel-btn">No</AlertDialogCancel>
            <AlertDialogAction onClick={logout} data-testid="logout-confirm-btn">
              Yes, Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
